import React from 'react';
import { Link } from 'react-router-dom';
// Import some nice, clean icons
import { FiCalendar, FiClock, FiMapPin, FiBriefcase, FiUser, FiMail, FiPhone, FiSearch } from 'react-icons/fi';

const JobCard = ({ job }) => {
  // NOTE: Ensure `jobs.activeYN` is in your backend SELECT statement
  // for the "Active/Inactive" status to work.
  const {
    headlineOnly, email1, phone1, firstName1, lastName1,
    mainContactFirstName, mainContactEmail, mainContactPhone,
    activation_date, title, location, expiration_date,
    company_name, featured, id, activeYN
  } = job;

  const isExpired = expiration_date && new Date(expiration_date) < new Date();

  return (
    <div className="bg-white p-4 space-y-3">
      {/* ===== 1. PRIMARY INFO ===== */}
      <div>
        <Link
          to={`/jobs/edit/${id}/`}
          className="block text-lg font-bold text-blue-700 hover:underline leading-tight"
        >
          {title}
        </Link>
        <div className="flex items-center gap-x-4 text-sm text-gray-600 mt-1">
          <div className="flex items-center gap-1.5">
            <FiBriefcase />
            <span>{company_name}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <FiMapPin />
            <span>{location}</span>
          </div>
        </div>
      </div>

      {/* ===== 2. STATUS TAGS ===== */}
      <div className="flex flex-wrap items-center gap-2">
        {featured ? (
          <span className="px-2 py-0.5 text-xs font-semibold text-purple-800 bg-purple-100 rounded-full">
            Featured
          </span>
        ) : null}
        {email1 && (
          <span className="px-2 py-0.5 text-xs font-semibold text-red-800 bg-red-100 rounded-full">
            Sales Lead
          </span>
        )}

        <span className="px-2 py-0.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-full">
          {headlineOnly ? "Headline Only" : "Full Job"}
        </span>
      </div>

      {/* ===== 3. DATE INFORMATION ===== */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 pt-2 border-t border-gray-100">
        <div className="flex items-center gap-1.5">
          <FiCalendar />
          <span>Posted: {new Date(activation_date || job.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
        </div>
        {expiration_date && (
          <div className={`flex items-center gap-1.5 font-semibold ${isExpired ? 'text-red-500' : 'text-gray-600'}`}>
            <FiClock />
            <span>{isExpired ? 'Expired:' : 'Expires:'} {new Date(expiration_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
          </div>
        )}
      </div>
      
      {/* ===== 4. CONTACT DETAILS ===== */}
      <div className="pt-3 border-t border-gray-100 text-sm text-gray-700 space-y-1">
        {phone1 || email1 ? ( // Contact details from the job post
          <>
            {(firstName1 || lastName1) && <div className="flex items-center gap-2"><FiUser className="text-gray-400" /> {firstName1} {lastName1}</div>}
            {email1 && <div className="flex items-center gap-2"><FiMail className="text-gray-400" /> <a href={`mailto:${email1}`} className="text-blue-600 hover:underline">{email1}</a></div>}
            {phone1 && <div className="flex items-center gap-2"><FiPhone className="text-gray-400" /> <a href={`tel:${phone1}`} className="text-blue-600 hover:underline">{phone1}</a></div>}
          </>
        ) : mainContactPhone || mainContactEmail ? ( // Main contact from employer record
          <>
            {mainContactFirstName && <div className="flex items-center gap-2"><FiUser className="text-gray-400" /> {mainContactFirstName} (Main)</div>}
            {mainContactEmail && <div className="flex items-center gap-2"><FiMail className="text-gray-400" /> <a href={`mailto:${mainContactEmail}`} className="text-blue-600 hover:underline">{mainContactEmail}</a></div>}
            {mainContactPhone && <div className="flex items-center gap-2"><FiPhone className="text-gray-400" /> <a href={`tel:${mainContactPhone}`} className="text-blue-600 hover:underline">{mainContactPhone}</a></div>}
          </>
        ) : ( // Fallback to a search link
          <div className="flex items-center gap-2">
            <FiSearch className="text-gray-400" />
            <a href={`https://www.google.com/search?q=${encodeURIComponent(`${company_name} ${location} contacts`)}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              Find Contacts
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobCard;